import { observer } from "mobx-react-lite";
import { Layer, Source } from "react-map-gl";
import { useAuthStore, useMapStore } from "../..";
import ILayerProps from "../../interfaces/IlayerProps";
import { ZoningFilterColor } from "../../interfaces/IZoningFilter";
import { minZoom } from "../mapConfig";

const sourceId = "regridZoningSource";
const sourceLayerId = "96d3f3ce056852c61f7504b641dfb07c156e2c74";
export const regridZoningInfoLayerId = "regridZoningInfoLayer";
export const regridIosZoningLayerId = "regridIosZoningShapesLayer";

function RegridZoningMapLayer({ zoningVisible, landUseVisible, iosZoningVisible }: IRegridZoningLinesMapLayerProps) {
    const authStore = useAuthStore();
    const mapStore = useMapStore();
    if (!mapStore.isStyleLoaded)
        return null;

    return (
        <Source
            id={sourceId}
            type="vector"
            tiles={[
                `https://tiles.regrid.com/api/v1/sources/zoning/layers/${sourceLayerId}/{z}/{x}/{y}.mvt?token=${authStore.publicRegridAccessToken}`
            ]}
            minzoom={Math.min(
                minZoom.zoning,
                minZoom.zoningLabels,
                minZoom.landUse,
                minZoom.landUseLabels,
                minZoom.iosZoning,
            )}
        >
            <InfoLayer />
            <ZoningLayer visible={zoningVisible} />
            <LandUseLayer visible={landUseVisible} />
            <IosZoningLayer visible={iosZoningVisible} />
        </Source>
    );
}

export default observer(RegridZoningMapLayer);

interface IRegridZoningLinesMapLayerProps {
    zoningVisible: boolean;
    landUseVisible: boolean;
    iosZoningVisible: boolean;
}

const InfoLayer = () => (
    <Layer
        id={regridZoningInfoLayerId}
        type="fill"
        source={sourceId}
        source-layer={sourceLayerId}
        paint={{
            "fill-color": "transparent",
        }}
    />
);

const ZoningLayer = ({ visible }: ILayerProps) => (
    <>
        <Layer
            id="regridZoningLinesLayer"
            type="line"
            source={sourceId}
            source-layer={sourceLayerId}
            minzoom={minZoom.zoning}
            paint={{
                "line-color": "hsl(239, 100%, 74%)",
            }}
            layout={{
                "visibility": visible ? "visible" : "none",
            }}
        />
        <Layer
            id="regridZoningSymbolsLayer"
            type="symbol"
            source={sourceId}
            source-layer={sourceLayerId}
            minzoom={minZoom.zoningLabels}
            paint={{
                "text-color": "hsl(239, 0%, 100%)",
                "text-halo-blur": 0.5,
                "text-halo-color": "hsl(239, 100%, 77%)",
                "text-halo-width": 0.5,
            }}
            layout={{
                "text-field": [
                    "to-string",
                    ["get", "zoning"],
                ],
                "visibility": visible ? "visible" : "none",
            }}
        />
    </>
);

const LandUseLayer = ({ visible }: ILayerProps) => (
    <>
        <Layer
            id="regridLandUseShapesLayer"
            type="fill"
            source={sourceId}
            source-layer={sourceLayerId}
            minzoom={minZoom.landUse}
            paint={{
                "fill-color": [
                    "match",
                    ["get", "zoning_type"],
                    "Commercial", "red",
                    "Residential", "yellow",
                    "Mixed", "purple",
                    "Special", "#FF33A1",
                    "Industrial", "blue",
                    "Agriculture", "green",
                    "Planned", "orange",
                    "gray"
                ],
                "fill-opacity": 0.4,
            }}
            layout={{
                "visibility": visible ? "visible" : "none",
            }}
        />
        <Layer
            id="regridLandUseLinesLayer"
            type="line"
            source={sourceId}
            source-layer={sourceLayerId}
            minzoom={minZoom.landUse}
            paint={{
                "line-width": 0.5,
                "line-color": "white",
            }}
            layout={{
                "visibility": visible ? "visible" : "none",
            }}
        />
        <Layer
            id="regridLandUseSymbolsLayer"
            type="symbol"
            source={sourceId}
            source-layer={sourceLayerId}
            minzoom={minZoom.landUseLabels}
            paint={{
                "text-color": "hsl(0, 0.00%, 2.70%)",
                "text-halo-blur": 0.5,
                "text-halo-color": "white",
                "text-halo-width": 1.5,
            }}
            layout={{
                "text-field": [
                    "to-string",
                    ["get", "zoning_type"],
                ],
                "visibility": visible ? "visible" : "none",
            }}
        />
    </>
);

const IosZoningLayer = ({ visible }: ILayerProps) => {
    const mapStore = useMapStore();

    return (
        <>
            {mapStore.zoningFilters.map(filter => (
                <Layer
                    key={filter.name}
                    id={`${regridIosZoningLayerId}_${filter.name}`}
                    type="fill"
                    source={sourceId}
                    source-layer={sourceLayerId}
                    minzoom={minZoom.iosZoning}
                    filter={[
                        "all",
                        ["match", ["get", "geoid"], [filter.geoid], true, false],
                        ["match", ["get", "zoning"], filter.zonings, true, false],
                        filter.municipality
                            ? ["match", ["get", "municipality_name"], [filter.municipality], true, false]
                            : undefined as any,
                    ].filter(f => f)}
                    paint={{
                        "fill-color": filter.color === ZoningFilterColor.Normal ? "hsla(219, 93%, 52%, 0.67)" : "hsla(219, 85%, 82%, 0.67)",
                        "fill-opacity": visible ? [
                            "interpolate",
                            ["exponential", 0.5],
                            ["zoom"],
                            14, 1,
                            16, 0.2,
                            18, 0,
                        ] : 0,
                    }}
                />
            ))}
        </>
    );
}